import React, {useState, useEffect, useCallback} from 'react';
import {OutlinedInputProps, TextField, CircularProgress} from '@mui/material';
import {CreateSportObjectInput, SportObjectEntity, useGetCoordsByAddressLazyQuery} from "../../../../../graphql/graphQlApiHooks";
import {useStores} from "../../../../../store";
import {Theme} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme: Theme) => ({
    form: {
        height: '100%',
        overflow: 'auto',
        '& .input-wrapper': {
            '& .MuiFormControl-root': {
                marginBottom: '1em',
            },
            '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                '&.Mui-focused': {
                    color: 'white',
                }
            },
            '& .MuiFilledInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: theme.shape.borderRadius,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
                '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                '& .MuiFilledInput-input': {
                    color: 'white',
                    padding: '16px 12px',
                    fontSize: '14px',
                    '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        opacity: 1,
                    }
                }
            },
        }
    }
}));
function AddSportObject({...rest}) {
    const {settingsStore} = useStores();
    const classes = useStyles();
    const [getCoordsByAddress, { loading: coordsLoading }] = useGetCoordsByAddressLazyQuery();

    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [longitude, setLongitude] = useState<number | null>(null);
    const [latitude, setLatitude] = useState<number | null>(null);
    const [activeDirectoryGroup, setActiveDirectoryGroup] = useState('');
    const [nameExistsError, setNameExistsError] = useState<string | null>(null);

    useEffect(() => {
        setName('')
        setAddress('')
        setLongitude(null)
        setLatitude(null)
        setActiveDirectoryGroup('')
    }, [rest.open])

    const onInputName = ({target: {value}}: any) => {
        nameExistsError && setNameExistsError(null)
        setName(value)
    };
    
    const onInputAddress = ({target: {value}}: any) => setAddress(value);
    
    const onInputLongitude = ({target: {value}}: any) => setLongitude(value ? parseFloat(value) : null);
    const onInputLatitude = ({target: {value}}: any) => setLatitude(value ? parseFloat(value) : null);
    const onInputActiveDirectoryGroup = ({target: {value}}: any) => setActiveDirectoryGroup(value);

    // Функция для получения координат по адресу
    const fetchCoordsByAddress = useCallback(async (addressValue: string) => {
        if (!addressValue.trim()) {
            setLongitude(null);
            setLatitude(null);
            return;
        }

        try {
            const result = await getCoordsByAddress({
                variables: { address: addressValue.trim() }
            });

            if (result.data?.getCoordsByAddress) {
                const { longitude: lng, latitude: lat } = result.data.getCoordsByAddress;
                if (lng !== null && lat !== null) {
                    setLongitude(lng);
                    setLatitude(lat);
                } else {
                    // Если координаты не найдены, сбрасываем их
                    setLongitude(null);
                    setLatitude(null);
                }
            }
        } catch (error) {
            console.error('Ошибка при получении координат:', error);
            // При ошибке также сбрасываем координаты
            setLongitude(null);
            setLatitude(null);
        }
    }, [getCoordsByAddress]);

    // Дебаунс для автоматического получения координат
    useEffect(() => {
        if (!address.trim()) {
            // Если адрес пустой, сбрасываем координаты
            setLongitude(null);
            setLatitude(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            fetchCoordsByAddress(address);
        }, 1000); // 1 секунда дебаунс

        return () => clearTimeout(timeoutId);
    }, [address, fetchCoordsByAddress]);

    // Обработчик потери фокуса поля адреса
    const onAddressBlur = () => {
        if (address.trim()) {
            fetchCoordsByAddress(address);
        } else {
            // Если адрес пустой, сбрасываем координаты
            setLongitude(null);
            setLatitude(null);
        }
    };

    const handleSubmit = (e: any) => {
        e.preventDefault()

        if (name !== '') {
            const nameIsExists = settingsStore.sportObjects.find((obj:SportObjectEntity)=>obj.name === name)

            if (!!nameIsExists) {
                setNameExistsError('Название занято')
                return
            }

            // Координаты центра Москвы
            const moscowCoords = {
                longitude: 37.6173,
                latitude: 55.7558
            };

            // Если адреса нет или координаты не определены, используем координаты центра Москвы
            const finalLongitude = (address.trim() && longitude !== null) ? longitude : moscowCoords.longitude;
            const finalLatitude = (address.trim() && latitude !== null) ? latitude : moscowCoords.latitude;

            settingsStore.sportObjectCreate({
                name, 
                address, 
                longitude: finalLongitude, 
                latitude: finalLatitude, 
                activeDirectoryGroup
            } as CreateSportObjectInput).then((createdObject) => {
                // Автоматически переходим к созданному объекту
                if (createdObject?.id) {
                    settingsStore.setSportObject(createdObject.id);
                }
                rest.onAdded()
                setName('')
                setAddress('')
                setLongitude(null)
                setLatitude(null)
                setActiveDirectoryGroup('')
            }).catch((error) => {
                // Ошибка уже обработана в store
                console.error('Ошибка при создании объекта:', error);
            })
        }
    };


    return <>
        <form
            id="add_object"
            onSubmit={(e) => handleSubmit(e)}
            className={classes.form}
        >

            <div className="input-wrapper">
                <TextField
                    name='name'
                    id="new_name"
                    label="Название"
                    placeholder="Добавьте название"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={name}
                    onChange={onInputName}
                    fullWidth={true}
                    multiline={true}
                    maxRows={3}
                    InputProps={{disableUnderline: true} as Partial<OutlinedInputProps>}
                    helperText={nameExistsError}
                    error={!!nameExistsError}

                />
                <TextField
                    name='address'
                    id="new_address"
                    label="Адрес"
                    placeholder="Добавьте адрес"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={address}
                    onChange={onInputAddress}
                    onBlur={onAddressBlur}
                    fullWidth={true}
                    multiline={true}
                    maxRows={2}
                    InputProps={{disableUnderline: true} as Partial<OutlinedInputProps>}
                />
                <TextField
                    name='longitude'
                    id="new_longitude"
                    label="Долгота"
                    placeholder="0.000000"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={longitude || ''}
                    onChange={onInputLongitude}
                    type="number"
                    fullWidth={true}
                    InputProps={{
                        disableUnderline: true,
                        inputProps: { step: "any" },
                        endAdornment: coordsLoading ? (
                            <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        ) : null
                    } as Partial<OutlinedInputProps>}
                />
                <TextField
                    name='latitude'
                    id="new_latitude"
                    label="Широта"
                    placeholder="0.000000"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={latitude || ''}
                    onChange={onInputLatitude}
                    type="number"
                    fullWidth={true}
                    InputProps={{
                        disableUnderline: true,
                        inputProps: { step: "any" },
                        endAdornment: coordsLoading ? (
                            <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        ) : null
                    } as Partial<OutlinedInputProps>}
                />
                <TextField
                    name='activeDirectoryGroup'
                    id="activeDirectoryGroup"
                    label="Группа в Active Directory"
                    placeholder="Укажите группу в Active Directory"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={activeDirectoryGroup}
                    onChange={onInputActiveDirectoryGroup}
                    fullWidth={true}
                    InputProps={{disableUnderline: true} as Partial<OutlinedInputProps>}
                />

            </div>
        </form>
    </>;
}

AddSportObject.protoTypes = {}

export default AddSportObject;
